import { createContext, useCallback, useContext, useRef, useState } from 'react';
import { AuthUser } from '../types/user';
import { useLogin, useRefreshToken } from '../services/api/users/mutations';
import { UseMutationResult } from '@tanstack/react-query';

export const AuthContext = createContext<{
  authUser: AuthUser | null;
  isAuthenticated: boolean;
  loginMutation: UseMutationResult<
    { access: string; refresh: string },
    Error,
    { email: string; password: string }
  >;
  logout: () => void;
  getAccessToken: () => Promise<string>;
  refreshAccessToken: () => Promise<string>;
  refreshTokenIfNeeded: () => Promise<boolean>;
}>({
  authUser: null,
  isAuthenticated: false,
  loginMutation: {} as UseMutationResult<
    { access: string; refresh: string },
    Error,
    { email: string; password: string }
  >,
  logout: () => {},
  getAccessToken: () => Promise.resolve(''),
  refreshAccessToken: () => Promise.resolve(''),
  refreshTokenIfNeeded: () => Promise.resolve(false),
});

const parseJwt = (token: string) => {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch {
    return null;
  }
};

const isTokenExpired = (token: string) => {
  const payload = parseJwt(token);
  if (!payload || !payload.exp) return true;
  // exp is in seconds
  return Date.now() >= payload.exp * 1000;
};

const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

export const AuthProvider = (props: { children?: React.ReactNode }) => {
  const { children } = props;
  const [accessToken, setAccessToken] = useState<string | null>(() =>
    localStorage.getItem(ACCESS_TOKEN_KEY)
  );
  const [refreshToken, setRefreshToken] = useState<string | null>(() =>
    localStorage.getItem(REFRESH_TOKEN_KEY)
  );
  const [isRefreshing, setIsRefreshing] = useState(false);
  const authUser = accessToken ? parseJwt(accessToken) : null;

  const clearTokens = useCallback(() => {
    setAccessToken(null);
    setRefreshToken(null);
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }, []);

  const saveTokens = useCallback((access: string, refresh: string) => {
    setAccessToken(access);
    setRefreshToken(refresh);
    localStorage.setItem(ACCESS_TOKEN_KEY, access);
    localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
  }, []);

  const refreshAccessTokenMutation = useRefreshToken(
    ({ access }) => {
      saveTokens(access, refreshToken!);
    },
    (error) => {
      console.error(error);
      clearTokens();
    }
  );

  const refreshTokenIfNeeded = useCallback(async () => {
    if (!accessToken || !refreshToken) return false;
    if (!isTokenExpired(accessToken)) return true;
    if (isRefreshing) return false;

    try {
      setIsRefreshing(true);
      const { access } =
        await refreshAccessTokenMutation.mutateAsync(refreshToken);
      return !!access;
    } catch (error) {
      console.error('Failed to refresh token:', error);
      clearTokens();
      return false;
    } finally {
      setIsRefreshing(false);
    }
  }, [
    accessToken,
    refreshToken,
    isRefreshing,
    refreshAccessTokenMutation,
    clearTokens,
  ]);

  const isAuthenticated =
    !!accessToken &&
    (!isTokenExpired(accessToken) ||
      (!!refreshToken && !isTokenExpired(refreshToken)));

  const loginMutation = useLogin(
    ({ access, refresh }) => {
      saveTokens(access, refresh);
    },
    (error) => {
      console.error(error);
      clearTokens();
    }
  );

  const logout = useCallback(() => {
    clearTokens();
  }, [clearTokens]);

  const getAccessToken = useCallback(async () => {
    if (!accessToken) throw new Error('No access token');
    if (!isTokenExpired(accessToken)) return accessToken;
    const { access } = await refreshAccessTokenMutation.mutateAsync(
      refreshToken!
    );
    return access;
  }, [accessToken, refreshToken, refreshAccessTokenMutation]);

  // Refresh regardless of the access token's expiry, for when the API has
  // rejected a token that looks valid here. Concurrent callers share one
  // request; a failed refresh clears the tokens (see the mutation's onError).
  const refreshPromiseRef = useRef<Promise<string> | null>(null);
  const refreshAccessToken = useCallback(() => {
    if (!refreshToken) return Promise.reject(new Error('No refresh token'));
    refreshPromiseRef.current ??= refreshAccessTokenMutation
      .mutateAsync(refreshToken)
      .then(({ access }) => access)
      .finally(() => {
        refreshPromiseRef.current = null;
      });
    return refreshPromiseRef.current;
  }, [refreshToken, refreshAccessTokenMutation]);

  return (
    <AuthContext.Provider
      value={{
        authUser,
        isAuthenticated,
        loginMutation,
        logout,
        getAccessToken,
        refreshAccessToken,
        refreshTokenIfNeeded,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('Auth context is not provided');
  }
  return context;
};
